/**
 * Norwegian holidays + hours computation.
 * Mirrors /app/backend/services/norwegian_holidays.py logic.
 *
 * When the underlag date falls on a weekend or Norwegian public holiday,
 * all worked hours become overtime (0 normal, everything to OT).
 */

const NORMAL_CUTOFF_HOUR = 15; // 15:00 — hours after this are overtime

function easterSunday(year) {
  // Anonymous Gregorian algorithm
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(year, month - 1, day));
}

function addDays(date, days) {
  const d = new Date(date.getTime());
  d.setUTCDate(d.getUTCDate() + days);
  return d;
}

function isoOf(d) {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const holidayCache = new Map();

export function norwegianHolidays(year) {
  if (holidayCache.has(year)) return holidayCache.get(year);
  const easter = easterSunday(year);
  const days = new Set([
    isoOf(new Date(Date.UTC(year, 0, 1))),      // Nyttårsdag
    isoOf(addDays(easter, -3)),                  // Skjærtorsdag
    isoOf(addDays(easter, -2)),                  // Langfredag
    isoOf(easter),                               // Første påskedag
    isoOf(addDays(easter, 1)),                   // Andre påskedag
    isoOf(new Date(Date.UTC(year, 4, 1))),       // Arbeidernes dag (May 1)
    isoOf(addDays(easter, 39)),                  // Kristi himmelfartsdag
    isoOf(new Date(Date.UTC(year, 4, 17))),      // Grunnlovsdag (May 17)
    isoOf(addDays(easter, 49)),                  // Første pinsedag
    isoOf(addDays(easter, 50)),                  // Andre pinsedag
    isoOf(new Date(Date.UTC(year, 11, 25))),     // Første juledag
    isoOf(new Date(Date.UTC(year, 11, 26))),     // Andre juledag
  ]);
  holidayCache.set(year, days);
  return days;
}

/** Returns null if the given ISO date is a working day, or 'weekend' | 'holiday' otherwise. */
export function nonWorkingReason(isoDateStr) {
  if (!isoDateStr) return null;
  const parts = isoDateStr.slice(0, 10).split("-");
  if (parts.length !== 3) return null;
  const y = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);
  if (!y || !m || !day) return null;
  const d = new Date(Date.UTC(y, m - 1, day));
  const dow = d.getUTCDay(); // 0=Sun, 6=Sat
  if (dow === 0 || dow === 6) return "weekend";
  const iso = isoOf(d);
  if (norwegianHolidays(y).has(iso)) return "holiday";
  return null;
}

/** Compute {total, normal, overtime} hours for a worker row.
 * Rule:
 *  - Hours before 15:00 = normal
 *  - Hours after 15:00 = overtime
 *  - Lunch (-30 min) subtracted from normal first, spills to overtime if needed
 *  - On weekends / Norwegian public holidays: all hours become overtime
 */
export function computeHours(w, dateStr) {
  try {
    const fh = parseInt(w.from_h || "0", 10);
    const fm = parseInt(w.from_m || "0", 10);
    const th = parseInt(w.to_h || "0", 10);
    const tm = parseInt(w.to_m || "0", 10);
    const from = fh + fm / 60;
    const to = th + tm / 60;
    const gross = Math.max(0, to - from);
    const lunch = w.minus_lunch ? 0.5 : 0;
    const total = Math.max(0, gross - lunch);
    if (nonWorkingReason(dateStr)) {
      return { total, normal: 0, overtime: total };
    }
    const normalWindow = Math.max(0, Math.min(to, NORMAL_CUTOFF_HOUR) - from);
    const overtimeWindow = Math.max(0, to - Math.max(from, NORMAL_CUTOFF_HOUR));
    const normal = Math.max(0, normalWindow - lunch);
    const remainingLunch = Math.max(0, lunch - normalWindow);
    const overtime = Math.max(0, overtimeWindow - remainingLunch);
    return { total, normal, overtime };
  } catch {
    return { total: 0, normal: 0, overtime: 0 };
  }
}

export function fmtH(n) {
  return Number.isFinite(n) ? (Math.round(n * 100) / 100).toFixed(n % 1 === 0 ? 0 : 1) : "0";
}

export const NON_WORKING_LABELS = {
  no: { weekend: "⚠️ Helg — alle timer regnes som overtid", holiday: "⚠️ Helligdag — alle timer regnes som overtid" },
  en: { weekend: "⚠️ Weekend — all hours count as overtime", holiday: "⚠️ Public holiday — all hours count as overtime" },
  ro: { weekend: "⚠️ Weekend — toate orele sunt considerate overtime", holiday: "⚠️ Sărbătoare legală — toate orele sunt overtime" },
  pl: { weekend: "⚠️ Weekend — wszystkie godziny liczone jako nadgodziny", holiday: "⚠️ Święto — wszystkie godziny liczone jako nadgodziny" },
};
